import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CustomButton } from '@mindly/ui-components';
import { CancelSubscriptionButton, SubscriptionActionModalTitle } from '../CancelSubscriptionModal.style';
import { useIonAlert } from '@ionic/react';
import { useAnalytics } from 'src/shared';
import { ISubscription } from 'src/redux/subscriptions/types';
import { useTranslation } from 'react-i18next';

type ReasonStepProps = {
  subscription?: ISubscription | null;
  handleNextStep: () => void;
  handleBackStep: () => void;
  handleSetReason: (reason: string) => void;
};

const ReasonStep: FC<ReasonStepProps> = ({ subscription, handleNextStep, handleBackStep, handleSetReason }) => {
  const intl = useIntl();
  const { t } = useTranslation('subscriptions');
  const [present] = useIonAlert();
  const { trackPredefinedEvent } = useAnalytics();

  const [reason, setReason] = useState('');
  const [reasonId, setReasonId] = useState('');
  const whyCancelSubscriptionQuestions = [
    t('cancellation.reasons.1'),
    t('cancellation.reasons.2'),
    t('cancellation.reasons.3'),
    t('cancellation.reasons.4'),
    t('cancellation.reasons.5'),
    t('cancellation.reasons.6'),
  ];

  useEffect(() => {
    handleSetReason(reason);
  }, [reason]);

  const handleReasonChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setReason(e.target.value);
    setReasonId(e.target.id.split('-')[0]);
  }, []);

  const handleCancelSubscription = useCallback(() => {
    present({
      mode: 'ios',
      header: intl.formatMessage({
        id: 'general.please.confirm',
        defaultMessage: 'Будь-ласка підтвердіть',
      }),
      buttons: [
        {
          text: intl.formatMessage({
            id: 'general.cancel.btn',
            defaultMessage: 'Скасувати',
          }),
          cssClass: 'confirm-delete-button',
          handler: async () => {
            trackPredefinedEvent('SUBSCRIPTION_CANCEL_REASON_MODAL_CLICKED', {
              subscription_id: subscription?.id,
              cancel_reason: reason,
              cancel_reason_code: parseInt(reasonId),
              button_back_clicked: false,
            });
            handleNextStep();
          },
        },
        {
          text: intl.formatMessage({
            id: 'general.no',
            defaultMessage: 'Ні',
          }),
        },
      ],
    });
  }, [intl, reason, reasonId, subscription?.id, trackPredefinedEvent, handleNextStep]);

  const onHandleBackStep = useCallback(() => {
    trackPredefinedEvent('SUBSCRIPTION_CANCEL_REASON_MODAL_CLICKED', {
      subscription_id: subscription?.id,
      cancel_reason: reason,
      cancel_reason_code: parseInt(reasonId),
      button_back_clicked: true,
    });
    handleBackStep();
  }, [subscription?.id, reason, handleBackStep, reasonId]);

  return (
    <div className={'reason-wrapper'}>
      <SubscriptionActionModalTitle>
        <FormattedMessage id={'subscription.cancel.step.2.title'} defaultMessage={'Чому Ви хочете скасувати?'} />
      </SubscriptionActionModalTitle>
      <ul className={'reason-list'}>
        {whyCancelSubscriptionQuestions.map((i, index) => (
          <li key={index}>
            <label className={'custom-radio'}>
              {i}
              <input type="radio" name={'reason'} value={i} id={`${index}-reason`} onChange={handleReasonChange} />
              <span />
            </label>
          </li>
        ))}
      </ul>
      <CustomButton buttonType={'primary'} onClick={onHandleBackStep}>
        <FormattedMessage id={'general.back.btn'} defaultMessage={'Назад'} />
      </CustomButton>
      <CancelSubscriptionButton onClick={handleCancelSubscription} disabled={!reason}>
        <FormattedMessage id={'general.cancel.btn'} defaultMessage={'Скасувати'} />
      </CancelSubscriptionButton>
    </div>
  );
};

export default ReasonStep;
